import { apiClient } from "~/services/apiClient";
import { ENVS } from "~/utils";
import {
  clearShadowInviteLinkFromStorage,
  readShadowInviteLinkFromStorage,
} from "~/utils/shadowInviteStorage";

const ENSURE_TENANT_LOG = "[ensureTenantExists]";

interface EnsureTenantParams {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
}

interface EnsureTenantOptions {
  /** Required: DynamoDB ensure is done via Lambda (same behavior as property-mgmt ensureUserExists) */
  idToken: string;
}

/**
 * Ensures tenant exists via Lambda sign-in action only.
 */
export const ensureTenantExists = async (
  params: EnsureTenantParams,
  options: EnsureTenantOptions
): Promise<{ success: boolean; error?: unknown }> => {
  const { id, email, username, firstName, lastName } = params;

  try {
    if (!options.idToken) {
      console.error(ENSURE_TENANT_LOG, "missing idToken", { id });
      return {
        success: false,
        error: "idToken is required for DynamoDB tenant creation",
      };
    }

    if (!ENVS.lambda_api_url) {
      console.error(ENSURE_TENANT_LOG, "REACT_APP_LAMBDA_API_URL is empty");
    }

    const link = readShadowInviteLinkFromStorage();
    const signInBody: Record<string, unknown> = {
      id,
      email,
      username: username || email,
      firstName: firstName || "",
      lastName: lastName || "",
    };
    if (link) {
      signInBody.shadowTenantId = link.shadowTenantId;
      signInBody.leaseId = link.leaseId;
    }

    const result = await apiClient.action<{ success: boolean }>(
      "sign-in",
      signInBody
    );

    if (!result.success) {
      console.error(ENSURE_TENANT_LOG, "sign-in action failed", {
        error: result.error,
      });
      return {
        success: false,
        error: result.error ?? "Tenant sign-in failed",
      };
    }

    if (link) {
      clearShadowInviteLinkFromStorage();
    }
    console.info(ENSURE_TENANT_LOG, "sign-in action succeeded", { id, email });
    return { success: true };
  } catch (error) {
    console.error(ENSURE_TENANT_LOG, "exception during ensureTenantExists", {
      error,
      id,
      email,
    });
    return {
      success: false,
      error,
    };
  }
};
